import { supabase } from './supabase';
import { ActivityService } from './activity';
import type { Database } from './database.types';
import * as Sentry from '@sentry/react';

type Project = Database['public']['Tables']['projects']['Row'];
type ProjectInsert = Database['public']['Tables']['projects']['Insert'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];
type TeamMemberInsert = Database['public']['Tables']['team_members']['Insert'];

// Helper types for Supabase relationship queries
type UserRelation = {
  id: string;
  name: string;
  email: string;
  avatar?: string;
};

type ProjectWithOwner = Project & {
  users: UserRelation;
};

type TeamMemberWithUser = TeamMember & {
  users: UserRelation;
};

type AnalysisWithCreative = Database['public']['Tables']['analyses']['Row'] & {
  creatives: {
    name: string;
    project_id: string;
  };
};

export interface ProjectWithMembers extends Project {
  memberCount: number;
  members?: TeamMemberInfo[];
  owner?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  recentActivity?: {
    creativesCount: number;
    analysesCount: number;
    lastAnalysis?: string | null;
  };
}

export interface TeamMemberInfo {
  id: string;
  userId: string;
  role: string;
  permissions: {
    canAnalyze?: boolean;
    canUpload?: boolean;
    canInvite?: boolean;
    canManageProject?: boolean;
  };
  invitationStatus: string;
  invitedBy?: string;
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  joinedAt: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  status?: 'active' | 'draft' | 'completed' | 'paused' | 'approved' | 'warning' | 'rejected' | 'archived' | 'deleted';
  cover_image?: string;
  deadline?: string;
}

export interface InviteTeamMemberRequest {
  projectId: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  permissions?: {
    canAnalyze: boolean;
    canUpload: boolean;
    canInvite: boolean;
    canManageProject: boolean;
  };
}

export interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  totalCreatives: number;
  totalAnalyses: number;
  totalCreativesWithAnalyses: number;
  launchReadyCreatives: number;
  creativesNeedingReview: number;
  creativesNeedingRework: number;
  launchReadyRate: number;
  averagePerformanceScore: number;
  performanceTrend: number;
  // 7-day trend data
  totalAnalysesTrend: number;
  launchReadyCreativesTrend: number;
  launchReadyRateTrend: number;
  averagePerformanceScoreTrend: number;
  strongestProjects: Array<{
    id: string;
    name: string;
    strength: number;
    trend: number;
  }>;
  topPerformingCreatives: Array<{
    id: string;
    name: string;
    project: string;
    performanceScore: number;
    complianceScore: number;
    thumbnail: string;
    analysisId?: string;
  }>;
  platformStats: Array<{
    name: string;
    approved: number;
    total: number;
    color: string;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'project_created' | 'creative_uploaded' | 'analysis_completed' | 'member_invited';
    projectName: string;
    timestamp: string;
    details: string;
  }>;
}

export class ProjectService {
  /**
   * Create a new project
   */
  static async createProject(request: CreateProjectRequest): Promise<ProjectWithMembers> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const projectData: ProjectInsert = {
        name: request.name,
        description: request.description || null,
        status: request.status || 'active',
        cover_image: request.cover_image || null,
        deadline: request.deadline || null,
        user_id: user.id
      };

      const { data: project, error } = await supabase
        .from('projects')
        .insert(projectData)
        .select(`
          *,
          users!projects_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        throw new Error(`Failed to create project: ${error.message}`);
      }

      // Log project creation activity
      await ActivityService.logProjectCreated(project.id, project.name);

      return {
        ...project,
        memberCount: 1, // Creator is the first member
        owner: {
          ...((project as ProjectWithOwner).users),
          avatar: ((project as ProjectWithOwner).users.avatar) || undefined
        },
        recentActivity: {
          creativesCount: 0,
          analysesCount: 0
        }
      };
    } catch (error) {
      console.error('Create project error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'createProject',
          component: 'ProjectService',
        },
        extra: {
          projectName: request.name,
          description: request.description,
        },
      });
      throw error;
    }
  }

  /**
   * Get project by ID with members and activity
   */
  static async getProject(projectId: string): Promise<ProjectWithMembers | null> {
    try {
      const { data: project, error } = await supabase
        .from('projects')
        .select(`
          *,
          users!projects_user_id_fkey(id, name, email, avatar)
        `)
        .eq('id', projectId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }

      // Get team members
      const members = await this.getProjectMembers(projectId);

      // Get activity stats
      const recentActivity = await this.getProjectActivity(projectId);

      // Use actual member count from database - members array already includes all members including owner
      const memberCount = members.length > 0 ? members.length : 1;

      return {
        ...project,
        memberCount,
        members,
        owner: {
          ...((project as ProjectWithOwner).users),
          avatar: ((project as ProjectWithOwner).users.avatar) || undefined
        },
        recentActivity
      };
    } catch (error) {
      console.error('Get project error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getProject',
          component: 'ProjectService',
        },
        extra: {
          projectId,
        },
      });
      throw error;
    }
  }

  /**
   * List projects for the current user
   */
  static async getUserProjects(
    status?: 'active' | 'draft' | 'completed' | 'paused' | 'approved' | 'warning' | 'rejected' | 'archived' | 'deleted',
    limit: number = 50,
    offset: number = 0
  ): Promise<ProjectWithMembers[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current user data to check for organization
      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members' projects
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      let query = supabase
        .from('projects')
        .select(`
          *,
          users!projects_user_id_fkey(id, name, email, avatar)
        `)
        .in('user_id', userIds)
        .order('updated_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (status) {
        query = query.eq('status', status);
      }

      const { data: projects, error } = await query;

      if (error) {
        throw error;
      }

      // Get member counts and activity for all projects in batch (performance optimization)
      const projectIds = (projects || []).map(p => p.id);
      const [memberCounts, activityData] = await Promise.all([
        this.getBatchProjectMemberCounts(projectIds),
        this.getBatchProjectActivity(projectIds)
      ]);

      // Combine project data with batch-fetched info
      const projectsWithInfo = (projects || []).map(project => ({
        ...project,
        memberCount: memberCounts[project.id] || 1,
        owner: {
          ...((project as ProjectWithOwner).users),
          avatar: ((project as ProjectWithOwner).users.avatar) || undefined
        },
        recentActivity: activityData[project.id] || { creativesCount: 0, analysesCount: 0 }
      }));

      return projectsWithInfo;
    } catch (error) {
      console.error('Get user projects error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getUserProjects',
          component: 'ProjectService',
        },
      });
      throw error;
    }
  }

  /**
   * Get projects where user is a team member
   */
  static async getSharedProjects(
    limit: number = 50,
    offset: number = 0
  ): Promise<ProjectWithMembers[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: teamMemberships, error } = await supabase
        .from('team_members')
        .select(`
          project_id,
          role,
          permissions,
          projects!inner(
            *,
            users!projects_user_id_fkey(id, name, email, avatar)
          )
        `)
        .eq('user_id', user.id)
        .eq('invitation_status', 'accepted')
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      // Get member counts and activity for all projects in batch (performance optimization)
      const projectIds = (teamMemberships || [])
        .map(m => (m as any).projects?.id)
        .filter(id => id) as string[];
      const [memberCounts, activityData] = await Promise.all([
        this.getBatchProjectMemberCounts(projectIds),
        this.getBatchProjectActivity(projectIds)
      ]);

      // Combine project data with batch-fetched info
      const projects = (teamMemberships || []).map((membership: any) => {
        const project = membership.projects;
        return {
          ...project,
          memberCount: memberCounts[project.id] || 1,
          owner: {
            ...project.users,
            avatar: project.users.avatar || undefined
          },
          recentActivity: activityData[project.id] || { creativesCount: 0, analysesCount: 0 },
          userRole: membership.role
        };
      });

      return projects;
    } catch (error) {
      console.error('Get shared projects error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getSharedProjects',
          component: 'ProjectService',
        },
      });
      throw error;
    }
  }

  /**
   * Update project information
   */
  static async updateProject(
    projectId: string,
    updates: Partial<Pick<Project, 'name' | 'description' | 'status' | 'cover_image' | 'deadline'>>
  ): Promise<ProjectWithMembers> {
    try {
      // First get the current project data to check for changes
      const { data: currentProject, error: currentError } = await supabase
        .from('projects')
        .select('name, deadline')
        .eq('id', projectId)
        .single();

      if (currentError) {
        throw currentError;
      }

      const { data: project, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select(`
          *,
          users!projects_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        throw error;
      }

      // Check if deadline was changed and log activity
      if ('deadline' in updates) {
        const oldDeadline = currentProject.deadline;
        const newDeadline = updates.deadline;
        
        // Only log if the deadline actually changed
        if (oldDeadline !== newDeadline) {
          await ActivityService.logProjectDeadlineChanged(
            projectId,
            project.name,
            oldDeadline,
            newDeadline
          );
        }
      }

      const members = await this.getProjectMembers(projectId);
      const recentActivity = await this.getProjectActivity(projectId);

      // Use actual member count from database - members array already includes all members including owner
      const memberCount = members.length > 0 ? members.length : 1;

      return {
        ...project,
        memberCount,
        members,
        owner: {
          ...((project as ProjectWithOwner).users),
          avatar: ((project as ProjectWithOwner).users.avatar) || undefined
        },
        recentActivity
      };
    } catch (error) {
      console.error('Update project error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'updateProject',
          component: 'ProjectService',
        },
        extra: {
          projectId,
          updates: request,
        },
      });
      throw error;
    }
  }

  /**
   * Delete project and all associated data
   */
  static async deleteProject(projectId: string): Promise<void> {
    try {
      // First get creative IDs and delete all analyses for creatives in this project
      const { data: projectCreatives } = await supabase
        .from('creatives')
        .select('id')
        .eq('project_id', projectId);
      
      const creativeIds = projectCreatives?.map(c => c.id) || [];
      
      if (creativeIds.length > 0) {
        await supabase
          .from('analyses')
          .delete()
          .in('creative_id', creativeIds);
      }

      // Delete all creatives in the project
      await supabase
        .from('creatives')
        .delete()
        .eq('project_id', projectId);

      // Delete team members
      await supabase
        .from('team_members')
        .delete()
        .eq('project_id', projectId);

      // Finally delete the project
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Delete project error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'deleteProject',
          component: 'ProjectService',
        },
        extra: {
          projectId,
        },
      });
      throw error;
    }
  }

  /**
   * Get batch project member counts for multiple projects using optimized RPC function
   */
  static async getBatchProjectMemberCounts(projectIds: string[]): Promise<Record<string, number>> {
    if (projectIds.length === 0) return {};
    
    try {
      // Use the optimized RPC function to get member counts at once
      const { data, error } = await supabase.rpc('get_project_member_counts', {
        project_ids: projectIds
      });

      if (error) {
        throw error;
      }

      // Get project owners to add them to the count if they're not in team_members
      const { data: projects } = await supabase
        .from('projects')
        .select('id, user_id')
        .in('id', projectIds);

      const projectOwners = projects?.reduce((acc, project) => {
        acc[project.id] = project.user_id;
        return acc;
      }, {} as Record<string, string>) || {};

      // Transform the RPC response into the expected format
      const result: Record<string, number> = {};
      
      // Initialize all projects with minimum count (1)
      projectIds.forEach(projectId => {
        result[projectId] = 1; // At least the owner
      });

      // Update with actual data from RPC - the RPC already counts all members including owner
      (data || []).forEach((row: any) => {
        const memberCount = Number(row.member_count) || 0;
        // Use the actual count from database - no need to add 1 as owner is already included
        result[row.project_id] = memberCount > 0 ? memberCount : 1;
      });
      
      return result;
    } catch (error) {
      console.warn('Failed to get batch project member counts:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getBatchProjectMemberCounts',
          component: 'ProjectService',
        },
        extra: {
          projectIds,
        },
      });
      // Return 1 for each project on error (assuming owner only)
      return projectIds.reduce((acc, projectId) => {
        acc[projectId] = 1;
        return acc;
      }, {} as Record<string, number>);
    }
  }

  /**
   * Get project team members
   */
  static async getProjectMembers(projectId: string): Promise<TeamMemberInfo[]> {
    try {
      const { data: members, error } = await supabase
        .from('team_members')
        .select(`
          *,
          users!team_members_user_id_fkey(id, name, email, avatar)
        `)
        .eq('project_id', projectId);

      if (error) {
        throw error;
      }

      return (members || []).map(member => ({
        id: member.id,
        userId: member.user_id,
        role: member.role || 'member',
        permissions: (member.permissions as any) || {},
        invitationStatus: member.invitation_status || 'pending',
        invitedBy: member.invited_by || undefined,
        user: {
          ...((member as TeamMemberWithUser).users),
          avatar: ((member as TeamMemberWithUser).users.avatar) || undefined
        },
        joinedAt: member.created_at || ''
      }));
    } catch (error) {
      console.error('Get project members error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getProjectMembers',
          component: 'ProjectService',
        },
        extra: {
          projectId,
        },
      });
      throw error;
    }
  }

  /**
   * Invite team member to project
   */
  static async inviteTeamMember(request: InviteTeamMemberRequest): Promise<TeamMemberInfo> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user exists
      const { data: targetUser, error: userLookupError } = await supabase
        .from('users')
        .select('id, name, email, avatar')
        .eq('email', request.email)
        .single();

      if (userLookupError) {
        throw new Error('User not found. They may need to create an account first.');
      }

      const defaultPermissions = {
        canAnalyze: request.role !== 'viewer',
        canUpload: request.role !== 'viewer',
        canInvite: request.role === 'admin',
        canManageProject: request.role === 'admin'
      };

      const memberData: TeamMemberInsert = {
        project_id: request.projectId,
        user_id: targetUser.id,
        role: request.role,
        permissions: request.permissions || defaultPermissions,
        invitation_status: 'pending',
        invited_by: user.id
      };

      const { data: member, error } = await supabase
        .from('team_members')
        .insert(memberData)
        .select(`
          *,
          users!team_members_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('User is already a member of this project');
        }
        throw error;
      }

      // TODO: Send invitation email here

      return {
        id: member.id,
        userId: member.user_id,
        role: member.role || 'member',
        permissions: (member.permissions as any) || {},
        invitationStatus: member.invitation_status || 'pending',
        invitedBy: member.invited_by || undefined,
        user: {
          ...((member as TeamMemberWithUser).users),
          avatar: ((member as TeamMemberWithUser).users.avatar) || undefined
        },
        joinedAt: member.created_at || ''
      };
    } catch (error) {
      console.error('Invite team member error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'inviteTeamMember',
          component: 'ProjectService',
        },
        extra: {
          projectId,
          email,
          role,
        },
      });
      throw error;
    }
  }

  /**
   * Accept team invitation
   */
  static async acceptInvitation(membershipId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ invitation_status: 'accepted' })
        .eq('id', membershipId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'acceptInvitation',
          component: 'ProjectService',
        },
        extra: {
          token,
        },
      });
      throw error;
    }
  }

  /**
   * Remove team member from project
   */
  static async removeTeamMember(membershipId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', membershipId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Remove team member error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'removeTeamMember',
          component: 'ProjectService',
        },
        extra: {
          projectId,
          memberId,
        },
      });
      throw error;
    }
  }

  /**
   * Update team member role and permissions
   */
  static async updateTeamMember(
    membershipId: string,
    updates: {
      role?: string;
      permissions?: {
        canAnalyze?: boolean;
        canUpload?: boolean;
        canInvite?: boolean;
        canManageProject?: boolean;
      };
    }
  ): Promise<TeamMemberInfo> {
    try {
      const { data: member, error } = await supabase
        .from('team_members')
        .update(updates)
        .eq('id', membershipId)
        .select(`
          *,
          users!team_members_user_id_fkey(id, name, email, avatar)
        `)
        .single();

      if (error) {
        throw error;
      }

      return {
        id: member.id,
        userId: member.user_id,
        role: member.role || 'member',
        permissions: (member.permissions as any) || {},
        invitationStatus: member.invitation_status || 'pending',
        invitedBy: member.invited_by || undefined,
        user: {
          ...((member as TeamMemberWithUser).users),
          avatar: ((member as TeamMemberWithUser).users.avatar) || undefined
        },
        joinedAt: member.created_at || ''
      };
    } catch (error) {
      console.error('Update team member error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'updateTeamMember',
          component: 'ProjectService',
        },
        extra: {
          projectId,
          memberId,
          role,
        },
      });
      throw error;
    }
  }

  /**
   * Get batch project activity and stats for multiple projects using optimized RPC function
   */
  static async getBatchProjectActivity(projectIds: string[]): Promise<Record<string, { creativesCount: number; analysesCount: number; lastAnalysis: string | null }>> {
    if (projectIds.length === 0) return {};
    
    try {
      // Use the optimized RPC function to get all stats at once
      const { data, error } = await supabase.rpc('get_project_stats', {
        project_ids: projectIds
      });

      if (error) {
        throw error;
      }

      // Transform the RPC response into the expected format
      const result: Record<string, { creativesCount: number; analysesCount: number; lastAnalysis: string | null }> = {};
      
      // Initialize all projects with default values
      projectIds.forEach(projectId => {
        result[projectId] = {
          creativesCount: 0,
          analysesCount: 0,
          lastAnalysis: null
        };
      });

      // Update with actual data from RPC
      (data || []).forEach((row: any) => {
        result[row.project_id] = {
          creativesCount: Number(row.creatives_count) || 0,
          analysesCount: Number(row.analyses_count) || 0,
          lastAnalysis: row.last_analysis || null
        };
      });
      
      return result;
    } catch (error) {
      console.warn('Failed to get batch project activity:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getBatchProjectActivity',
          component: 'ProjectService',
        },
        extra: {
          projectIds,
        },
      });
      // Return empty data for each project on error
      return projectIds.reduce((acc, projectId) => {
        acc[projectId] = {
          creativesCount: 0,
          analysesCount: 0,
          lastAnalysis: null
        };
        return acc;
      }, {} as Record<string, { creativesCount: number; analysesCount: number; lastAnalysis: string | null }>);
    }
  }

  /**
   * Get project activity and stats
   */
  static async getProjectActivity(projectId: string): Promise<{ creativesCount: number; analysesCount: number; lastAnalysis?: string | null }> {
    try {
      // Get creatives count
      const { count: creativesCount } = await supabase
        .from('creatives')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId);

      // Since analyses table doesn't have a direct link to projects yet,
      // we'll get creative URLs and match them with analyses
      const { data: creatives } = await supabase
        .from('creatives')
        .select('url')
        .eq('project_id', projectId);

      const creativeUrls = creatives?.map(c => c.url) || [];

      // Get analyses count only if we have creative URLs
      let analysesCount = 0;
      let lastAnalysis = null;

      if (creativeUrls.length > 0) {
        const { count } = await supabase
          .from('analyses')
          .select('*', { count: 'exact', head: true })
          .in('creative_url', creativeUrls);

        analysesCount = count || 0;

        // Get last analysis
        const { data: lastAnalysisData } = await supabase
          .from('analyses')
          .select('created_at')
          .in('creative_url', creativeUrls)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        lastAnalysis = lastAnalysisData?.created_at;
      }

      return {
        creativesCount: creativesCount || 0,
        analysesCount,
        lastAnalysis
      };
    } catch (error) {
      console.warn('Failed to get project activity:', error);
      return {
        creativesCount: 0,
        analysesCount: 0
      };
    }
  }

  /**
   * Search projects by name or description
   */
  static async searchProjects(
    query: string,
    limit: number = 20
  ): Promise<ProjectWithMembers[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: projects, error } = await supabase
        .from('projects')
        .select(`
          *,
          users!projects_user_id_fkey(id, name, email, avatar)
        `)
        .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      const projectsWithInfo = await Promise.all(
        (projects || []).map(async (project) => {
          const members = await this.getProjectMembers(project.id);
          const recentActivity = await this.getProjectActivity(project.id);

          // Use actual member count from database - members array already includes all members including owner
          const memberCount = members.length > 0 ? members.length : 1;

          return {
            ...project,
            memberCount,
            owner: {
              ...((project as ProjectWithOwner).users),
              avatar: ((project as ProjectWithOwner).users.avatar) || undefined
            },
            recentActivity
          };
        })
      );

      return projectsWithInfo;
    } catch (error) {
      console.error('Search projects error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'searchProjects',
          component: 'ProjectService',
        },
        extra: {
          query,
        },
      });
      throw error;
    }
  }

  /**
   * Get dashboard statistics for current user
   */
  static async getDashboardStats(): Promise<ProjectStats> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get current user data to check for organization and team role
      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, handle team member redirection
      if (currentUser?.organization_id) {
        if (currentUser.team_role) {
          // This is a team member - check if they're not the organization owner
          const { data: orgData } = await supabase
            .from('organizations')
            .select('created_by')
            .eq('id', currentUser.organization_id)
            .single();
          
          if (orgData?.created_by && orgData.created_by !== user.id) {
            // This is a team member (not organization owner) - show organization owner's data only
            console.log('🔄 Team member detected in dashboard stats - showing organization owner data:', {
              teamMember: user.id,
              organizationOwner: orgData.created_by
            });
            userIds = [orgData.created_by];
          } else {
            // This is the organization owner - include all organization members' projects
            const { data: orgMembers } = await supabase
              .from('users')
              .select('id')
              .eq('organization_id', currentUser.organization_id);
            
            if (orgMembers && orgMembers.length > 0) {
              userIds = orgMembers.map(m => m.id);
            }
          }
        } else {
          // User is in organization but has no team role - include all organization members
          const { data: orgMembers } = await supabase
            .from('users')
            .select('id')
            .eq('organization_id', currentUser.organization_id);
          
          if (orgMembers && orgMembers.length > 0) {
            userIds = orgMembers.map(m => m.id);
          }
        }
      }

      // Get projects from user or organization members
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id, name')
        .in('user_id', userIds);

      const projectIds = userProjects?.map(p => p.id) || [];
      const projectMap = Object.fromEntries(userProjects?.map(p => [p.id, p.name]) || []);

      // Parallel data fetching for performance
      const [
        projectCounts,
        creativesData,
        analysesData,
        platformData,
        performanceData,
        trendData
      ] = await Promise.all([
        this.getProjectCounts(userIds),
        this.getCreativesData(projectIds),
        this.getAnalysesData(projectIds, userIds),
        this.getPlatformStats(projectIds, userIds),
        this.getPerformanceStats(projectIds, projectMap, userIds),
        this.get7DayTrends(projectIds)
      ]);

      // Calculate average quality score from all analyzed creatives
      const averageQualityScore = analysesData.averageScore || 0;

      // Get recent activity
      const recentActivity = await this.getRecentActivity(userIds, projectMap);

      const dashboardStats = {
        totalProjects: projectCounts.total,
        activeProjects: projectCounts.active,
        totalCreatives: creativesData.total,
        totalAnalyses: analysesData.totalAnalyses,
        totalCreativesWithAnalyses: analysesData.totalCreativesWithAnalyses || 0,
        launchReadyCreatives: analysesData.launchReadyCreatives || 0,
        creativesNeedingReview: analysesData.creativesNeedingReview || 0,
        creativesNeedingRework: analysesData.creativesNeedingRework || 0,
        launchReadyRate: averageQualityScore,
        averagePerformanceScore: performanceData.averageScore,
        performanceTrend: performanceData.trend,
        // 7-day trends
        totalAnalysesTrend: trendData.totalAnalysesTrend,
        launchReadyCreativesTrend: trendData.launchReadyCreativesTrend,
        launchReadyRateTrend: trendData.launchReadyRateTrend,
        averagePerformanceScoreTrend: trendData.averagePerformanceScoreTrend,
        strongestProjects: performanceData.strongestProjects,
        topPerformingCreatives: performanceData.topPerformingCreatives,
        platformStats: platformData,
        recentActivity
      };
      
      return dashboardStats;
    } catch (error) {
      console.error('Get dashboard stats error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'getDashboardStats',
          component: 'ProjectService',
        },
      });
      throw error;
    }
  }

  private static async getProjectCounts(userIds: string[]) {
    const [totalResult, activeResult] = await Promise.all([
      supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .in('user_id', userIds),
      supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .in('user_id', userIds)
        .eq('status', 'active')
    ]);

    return {
      total: totalResult.count || 0,
      active: activeResult.count || 0
    };
  }

  private static async getCreativesData(projectIds: string[]) {
    if (projectIds.length === 0) {
      return { total: 0 };
    }

    const { count } = await supabase
      .from('creatives')
      .select('*', { count: 'exact', head: true })
      .in('project_id', projectIds);

    return { total: count || 0 };
  }

  private static async getAnalysesData(projectIds: string[], targetUserIds: string[] = []) {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    let allAnalyses: any[] = [];

    // Get project-based analyses if we have projects
    if (projectIds.length > 0) {
      // Get creatives from projects
      const { data: creatives } = await supabase
        .from('creatives')
        .select('id')
        .in('project_id', projectIds);

      const creativeIds = creatives?.map(c => c.id) || [];

      if (creativeIds.length > 0) {
        // Get analyses with scores for project creatives
        const { data: projectAnalyses } = await supabase
          .from('analyses')
          .select('id, overall_score, creative_id')
          .in('creative_id', creativeIds)
          .not('overall_score', 'is', null);

        allAnalyses = projectAnalyses || [];
      }
    }

    // Get direct user analyses (not linked to any creative/project)
    // Use targetUserIds if provided (for team members), otherwise use current user
    const userIdsForAnalyses = targetUserIds.length > 0 ? targetUserIds : [user.id];
    
    const { data: directAnalyses } = await supabase
      .from('analyses')
      .select('id, overall_score, creative_id')
      .in('user_id', userIdsForAnalyses)
      .not('overall_score', 'is', null);

    // Merge project-based and direct analyses, avoiding duplicates
    const existingAnalysisIds = new Set(allAnalyses.map(a => a.id));
    (directAnalyses || []).forEach(analysis => {
      if (!existingAnalysisIds.has(analysis.id)) {
        allAnalyses.push(analysis);
      }
    });

    if (!allAnalyses || allAnalyses.length === 0) {
      return {
        totalAnalyses: 0,
        totalCreativesWithAnalyses: 0,
        launchReadyCreatives: 0,
        creativesNeedingReview: 0,
        creativesNeedingRework: 0,
        averageScore: 0
      };
    }

    // Group analyses by creative_id to get the latest/best score for each creative
    const creativeScores = new Map<string, number>();
    
    allAnalyses.forEach(analysis => {
      const creativeId = analysis.creative_id || analysis.id; // Use analysis ID if no creative_id (for direct analyses)
      const score = analysis.overall_score || 0;
      
      // Keep the highest score for each creative (most optimistic view)
      if (creativeId && (!creativeScores.has(creativeId) || score > (creativeScores.get(creativeId) || 0))) {
        creativeScores.set(creativeId, score);
      }
    });

    // Use actionable quality categories: 80+ = launch ready, 60-79 = needs review, <60 = needs rework
    const totalAnalyses = allAnalyses.length;
    const totalCreativesWithAnalyses = creativeScores.size;
    const creativeScoresArray = Array.from(creativeScores.values());
    
    const launchReadyCreatives = creativeScoresArray.filter(score => score >= 80).length;
    const creativesNeedingReview = creativeScoresArray.filter(score => score >= 60 && score < 80).length;
    const creativesNeedingRework = creativeScoresArray.filter(score => score < 60).length;
    
    // Calculate average score from unique creatives
    const averageScore = creativeScoresArray.length > 0 
      ? Math.round(creativeScoresArray.reduce((sum, score) => sum + score, 0) / creativeScoresArray.length)
      : 0;

    const result = {
      totalAnalyses,
      totalCreativesWithAnalyses,
      launchReadyCreatives,
      creativesNeedingReview,
      creativesNeedingRework,
      averageScore
    };
    
    return result;
  }

  private static async getPlatformStats(projectIds: string[], userIds: string[] = []) {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    let allAnalysisIds: string[] = [];

    // Get project-based analyses if we have projects
    if (projectIds.length > 0) {
      // Get creatives from projects
      const { data: creatives } = await supabase
        .from('creatives')
        .select('id')
        .in('project_id', projectIds);

      const creativeIds = creatives?.map(c => c.id) || [];

      if (creativeIds.length > 0) {
        // Get analyses for platform stats
        const { data: projectAnalyses } = await supabase
          .from('analyses')
          .select('id')
          .in('creative_id', creativeIds);

        allAnalysisIds = projectAnalyses?.map(a => a.id) || [];
      }
    }

    // Get direct user analyses - use userIds if provided (for team members)
    const targetUserIds = userIds.length > 0 ? userIds : [user.id];
    const { data: directAnalyses } = await supabase
      .from('analyses')
      .select('id')
      .in('user_id', targetUserIds);

    // Merge project-based and direct analyses, avoiding duplicates
    const existingAnalysisIds = new Set(allAnalysisIds);
    (directAnalyses || []).forEach(analysis => {
      if (!existingAnalysisIds.has(analysis.id)) {
        allAnalysisIds.push(analysis.id);
      }
    });
    
    if (allAnalysisIds.length === 0) {
      return [];
    }

    // Get analyses with platform data from the analysis_data JSONB field
    const { data: analysesWithPlatforms } = await supabase
      .from('analyses')
      .select('id, analysis_data, created_at')
      .in('id', allAnalysisIds)
      .not('analysis_data', 'is', null);

    if (!analysesWithPlatforms || analysesWithPlatforms.length === 0) {
      return [];
    }

    // Extract platform scores from analysis_data JSONB
    const platformStatsMap = new Map<string, { approved: number; total: number; totalScore: number; avgScore: number }>();

    analysesWithPlatforms.forEach(analysis => {
      const analysisData = analysis.analysis_data;
      if (analysisData && analysisData.platforms) {
        Object.entries(analysisData.platforms).forEach(([platform, data]: [string, any]) => {
          // Use complianceScore as the main score for platform performance
          const score = data.complianceScore || 0;
          
          if (!platformStatsMap.has(platform)) {
            platformStatsMap.set(platform, { approved: 0, total: 0, totalScore: 0, avgScore: 0 });
          }
          
          const stats = platformStatsMap.get(platform)!;
          stats.total += 1;
          stats.totalScore += score;
          
          // Count as approved if compliance score >= 80
          if (score >= 80) {
            stats.approved += 1;
          }
        });
      }
    });

    // Calculate average scores
    platformStatsMap.forEach((stats) => {
      stats.avgScore = stats.total > 0 ? Math.round(stats.totalScore / stats.total) : 0;
    });

    if (platformStatsMap.size === 0) {
      return [];
    }

    // Verify platform data exists

    // Platform colors mapping (updated to match database values)
    const platformColors = {
      'Meta': 'bg-blue-500',
      'Google Ads': 'bg-red-500',
      'Google': 'bg-red-500',
      'TikTok': 'bg-black',
      'YouTube': 'bg-red-600',
      'Pinterest': 'bg-red-500',
      'Microsoft Ads': 'bg-blue-600',
      'Microsoft': 'bg-blue-600'
    };

    // Convert to array format
    const platformStats = Array.from(platformStatsMap.entries())
      .map(([name, stats]) => ({
        name,
        approved: stats.approved,
        total: stats.total,
        avgScore: stats.avgScore,
        color: platformColors[name as keyof typeof platformColors] || 'bg-gray-500'
      }))
      .sort((a, b) => b.total - a.total); // Sort by total analyses desc

    return platformStats;
  }

    private static async getPerformanceStats(projectIds: string[], projectMap: Record<string, string>, userIds: string[] = []) {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    try {
      let allCreatives: any[] = [];
      let allAnalyses: any[] = [];

      // Get project-based creatives and analyses if we have projects
      if (projectIds.length > 0) {
        // Step 1: Get creatives for the projects
        const { data: projectCreatives } = await supabase
          .from('creatives')
          .select('id, name, project_id, url')
          .in('project_id', projectIds);

        allCreatives = projectCreatives || [];

        const creativeIds = projectCreatives?.map(c => c.id) || [];

        if (creativeIds.length > 0) {
          // Step 2: Get analyses for those creatives
          // Match by creative_id, creative_url, or file_name to handle data integrity issues
          const creativeUrls = projectCreatives?.map(c => c.url).filter(url => url) || [];
          const creativeNames = projectCreatives?.map(c => c.name).filter(name => name) || [];
          
          // First try to get analyses by creative_id
          let { data: projectAnalyses } = await supabase
            .from('analyses')
            .select('id, overall_score, creative_id, created_at, creative_url, file_name')
            .in('creative_id', creativeIds)
            .not('overall_score', 'is', null)
            .order('created_at', { ascending: false });

          let allProjectAnalyses = projectAnalyses || [];

          // Also get analyses by URL matching if we have URLs
          if (creativeUrls.length > 0) {
            const { data: urlAnalyses } = await supabase
              .from('analyses')
              .select('id, overall_score, creative_id, created_at, creative_url, file_name')
              .in('creative_url', creativeUrls)
              .not('overall_score', 'is', null)
              .order('created_at', { ascending: false });
            
            // Merge avoiding duplicates
            const existingIds = new Set(allProjectAnalyses.map(a => a.id));
            (urlAnalyses || []).forEach(analysis => {
              if (!existingIds.has(analysis.id)) {
                allProjectAnalyses.push(analysis);
              }
            });
          }

          // Also get analyses by filename matching if we have names
          if (creativeNames.length > 0) {
            const { data: nameAnalyses } = await supabase
              .from('analyses')
              .select('id, overall_score, creative_id, created_at, creative_url, file_name')
              .in('file_name', creativeNames)
              .not('overall_score', 'is', null)
              .order('created_at', { ascending: false });
            
            // Merge avoiding duplicates
            const existingIds = new Set(allProjectAnalyses.map(a => a.id));
            (nameAnalyses || []).forEach(analysis => {
              if (!existingIds.has(analysis.id)) {
                allProjectAnalyses.push(analysis);
              }
            });
          }

          allAnalyses = allProjectAnalyses;
        }
      }

      // Get direct user analyses - use userIds if provided (for team members)
      const targetUserIds = userIds.length > 0 ? userIds : [user.id];
      const { data: directAnalyses } = await supabase
        .from('analyses')
        .select('id, overall_score, creative_id, created_at, creative_url, file_name')
        .in('user_id', targetUserIds)
        .not('overall_score', 'is', null)
        .order('created_at', { ascending: false });

      // Get all creatives that belong to direct analyses (including those with project_id: null)
      const directCreativeIds = (directAnalyses || [])
        .map(a => a.creative_id)
        .filter(id => id !== null);
        
      if (directCreativeIds.length > 0) {
        const { data: directCreatives } = await supabase
          .from('creatives')
          .select('id, name, project_id, url')
          .in('id', directCreativeIds);
          
        // Add direct creatives to allCreatives, avoiding duplicates
        const existingCreativeIds = new Set(allCreatives.map(c => c.id));
        (directCreatives || []).forEach(creative => {
          if (!existingCreativeIds.has(creative.id)) {
            allCreatives.push(creative);
          }
        });
      }

      // Merge project-based and direct analyses, avoiding duplicates
      const existingAnalysisIds = new Set(allAnalyses.map(a => a.id));
      (directAnalyses || []).forEach(analysis => {
        if (!existingAnalysisIds.has(analysis.id)) {
          allAnalyses.push(analysis);
          
          // Create a virtual creative for direct analyses that don't have a creative_id
          if (!analysis.creative_id) {
            allCreatives.push({
              id: analysis.id, // Use analysis ID as creative ID
              name: analysis.file_name || 'Unnamed Creative',
              project_id: null,
              url: analysis.creative_url
            });
          }
        }
      });

      if (!allAnalyses || allAnalyses.length === 0) {
        return {
          averageScore: 0,
          trend: 0,
          strongestProjects: [],
          topPerformingCreatives: []
        };
      }

      // Step 3: Combine creative and analysis data
      const creativesWithAnalyses = allCreatives.map(creative => {
        const creativeAnalyses = allAnalyses.filter(a => {
          // Match by creative_id first (most reliable)
          if (a.creative_id === creative.id) return true;
          
          // Match by URL (for orphaned analyses)
          if (a.creative_url && creative.url && a.creative_url === creative.url) return true;
          
          // Match by filename (for orphaned analyses)
          if (a.file_name && creative.name && a.file_name === creative.name) return true;
          
          // For direct analyses without creative_id, match by ID
          if (!a.creative_id && a.id === creative.id) return true;
          
          return false;
        });
        return {
          ...creative,
          analyses: creativeAnalyses
        };
      }).filter(creative => creative.analyses.length > 0);

      if (creativesWithAnalyses.length > 0) {
        // Data is available for processing
      }

      // Calculate average performance score from real analyses
      const allScores = creativesWithAnalyses.flatMap(creative => 
        creative.analyses.map(analysis => analysis.overall_score || 0)
      );
      const averageScore = allScores.length > 0 
        ? Math.round(allScores.reduce((sum, score) => sum + score, 0) / allScores.length)
        : 0;

      // Calculate trend (last 10 vs previous 10 analyses)
      const sortedAnalyses = creativesWithAnalyses
        .flatMap(creative => creative.analyses.map(analysis => ({
          ...analysis,
          creative_id: creative.id,
          creative_name: creative.name
        })))
        .filter(analysis => analysis.created_at) // Filter out null dates
        .sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime());

      let trend = 0;
      if (sortedAnalyses.length >= 10) {
        const recent10 = sortedAnalyses.slice(0, 10);
        const previous10 = sortedAnalyses.slice(10, 20);
        
        if (previous10.length > 0) {
          const recentAvg = recent10.reduce((sum, a) => sum + (a.overall_score || 0), 0) / recent10.length;
          const previousAvg = previous10.reduce((sum, a) => sum + (a.overall_score || 0), 0) / previous10.length;
          trend = previousAvg > 0 ? Math.round(((recentAvg - previousAvg) / previousAvg) * 100) : 0;
        }
      }

      // Get strongest projects based on average scores
      const projectScoresMap = new Map<string, number[]>();
      creativesWithAnalyses.forEach(creative => {
        const projectId = creative.project_id;
        
        if (projectId && !projectScoresMap.has(projectId)) {
          projectScoresMap.set(projectId, []);
        }
        creative.analyses.forEach(analysis => {
          if (analysis.overall_score && projectId && projectScoresMap.has(projectId)) {
            projectScoresMap.get(projectId)!.push(analysis.overall_score);
          }
        });
      });

      const strongestProjects = Array.from(projectScoresMap.entries())
        .map(([projectId, scores]) => {
          const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
          // Calculate trend for this project (simplified)
          const projectTrend = scores.length >= 4 
            ? Math.round(((scores.slice(0, 2).reduce((sum, s) => sum + s, 0) / 2) - 
                        (scores.slice(-2).reduce((sum, s) => sum + s, 0) / 2)) / 10)
            : 0;
          
          return {
            id: projectId,
            name: projectMap[projectId] || 'Unknown Project',
            strength: Math.round(avgScore),
            trend: Math.max(-10, Math.min(10, projectTrend)) // Cap between -10 and +10
          };
        })
        .sort((a, b) => b.strength - a.strength)
        .slice(0, 3);

      // Get top performing creatives with latest analysis
      const topPerformingCreatives = creativesWithAnalyses
        .map(creative => {
          // Get the latest analysis for this creative
          const latestAnalysis = creative.analyses
            .filter(analysis => analysis.created_at) // Filter out null dates
            .sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime())[0];
          
          if (!latestAnalysis || !latestAnalysis.overall_score) return null;
          
          return {
            id: creative.id,
            name: creative.name || 'Unnamed Creative',
            project: (creative.project_id && projectMap[creative.project_id]) || 'Direct Analysis',
            performanceScore: latestAnalysis.overall_score,
            complianceScore: latestAnalysis.overall_score, // Using same score for now
            thumbnail: creative.url || 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=300',
            analysisId: latestAnalysis.id
          };
        })
        .filter(item => item !== null)
        .sort((a, b) => (b?.performanceScore || 0) - (a?.performanceScore || 0))
        .slice(0, 3);



      const result = {
        averageScore,
        trend,
        strongestProjects,
        topPerformingCreatives
      };
      
      return result;

    } catch (error) {
      console.error('Error getting performance stats:', error);
      // Return empty data on error
      return {
        averageScore: 0,
        trend: 0,
        strongestProjects: [],
        topPerformingCreatives: []
      };
    }
  }

  private static async getRecentActivity(userIds: string[], projectMap: Record<string, string>) {
    // Get recent activity from multiple sources
    const activities: ProjectStats['recentActivity'] = [];
    
    // Recent project creation
    const { data: recentProjects } = await supabase
      .from('projects')
      .select('id, name, created_at')
      .in('user_id', userIds)
      .order('created_at', { ascending: false })
      .limit(3);

    recentProjects?.forEach(project => {
      if (project.created_at) {
        activities.push({
          id: `project-${project.id}`,
          type: 'project_created',
          projectName: project.name,
          timestamp: project.created_at,
          details: 'New project created'
        });
      }
    });

    // Recent analyses
    const { data: recentAnalyses } = await supabase
      .from('analyses')
      .select(`
        id, 
        created_at,
        creatives!inner(name, project_id)
      `)
      .in('creatives.project_id', Object.keys(projectMap))
      .order('created_at', { ascending: false })
      .limit(5);

    recentAnalyses?.forEach(analysis => {
      if (analysis.created_at) {
        const creative = (analysis as AnalysisWithCreative).creatives;
        activities.push({
          id: `analysis-${analysis.id}`,
          type: 'analysis_completed',
          projectName: projectMap[creative.project_id] || 'Unknown Project',
          timestamp: analysis.created_at,
          details: `Analysis completed for ${creative.name}`
        });
      }
    });

    // Sort by timestamp and return latest 5
    return activities
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5);
  }

  private static async get7DayTrends(projectIds: string[]) {
    // Calculate week-over-week: this week (Monday-Sunday) vs last week (Monday-Sunday)
    const now = new Date();
    
    // Get this week's Monday
    const thisWeekStart = new Date(now);
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Handle Sunday as last day of week
    thisWeekStart.setDate(now.getDate() - daysFromMonday);
    thisWeekStart.setHours(0, 0, 0, 0);
    
    // This week ends on Sunday
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
    thisWeekEnd.setHours(23, 59, 59, 999);
    
    // Last week: Monday to Sunday of previous week
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    lastWeekEnd.setHours(23, 59, 59, 999);

    try {
      // Get current user to find organization members  
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get creatives from projects AND creatives directly owned by user/org members 
      const creativesQuery = supabase
        .from('creatives')
        .select('id');

      // Include creatives from projects OR creatives directly owned by user/org members
      if (projectIds.length > 0) {
        creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
      } else {
        // If no projects, only get creatives directly owned by user/org members
        creativesQuery
          .is('project_id', null)
          .in('user_id', userIds);
      }

      const { data: creatives } = await creativesQuery;
      const creativeIds = creatives?.map(c => c.id) || [];

      if (creativeIds.length === 0) {
        return {
          totalAnalysesTrend: 0,
          launchReadyCreativesTrend: 0,
          launchReadyRateTrend: 0,
          averagePerformanceScoreTrend: 0
        };
      }

      // Get this week's data
      const { data: thisWeekAnalyses } = await supabase
        .from('analyses')
        .select('id, overall_score, creative_id')
        .in('creative_id', creativeIds)
        .gte('created_at', thisWeekStart.toISOString())
        .lte('created_at', thisWeekEnd.toISOString())
        .not('overall_score', 'is', null);

      // Get last week's data  
      const { data: lastWeekAnalyses } = await supabase
        .from('analyses')
        .select('id, overall_score, creative_id')
        .in('creative_id', creativeIds)
        .gte('created_at', lastWeekStart.toISOString())
        .lte('created_at', lastWeekEnd.toISOString())
        .not('overall_score', 'is', null);

      // Calculate this week's metrics
      const thisWeekTotalAnalyses = thisWeekAnalyses?.length || 0;
      const thisWeekLaunchReadyCreatives = thisWeekAnalyses?.filter(a => (a.overall_score || 0) >= 80).length || 0;
      const thisWeekLaunchReadyRate = thisWeekTotalAnalyses > 0 ? (thisWeekLaunchReadyCreatives / thisWeekTotalAnalyses) * 100 : 0;
      const thisWeekAvgScore = thisWeekTotalAnalyses > 0 
        ? (thisWeekAnalyses?.reduce((sum, a) => sum + (a.overall_score || 0), 0) || 0) / thisWeekTotalAnalyses 
        : 0;

      // Calculate last week's metrics
      const lastWeekTotalAnalyses = lastWeekAnalyses?.length || 0;
      const lastWeekLaunchReadyCreatives = lastWeekAnalyses?.filter(a => (a.overall_score || 0) >= 80).length || 0;
      const lastWeekLaunchReadyRate = lastWeekTotalAnalyses > 0 ? (lastWeekLaunchReadyCreatives / lastWeekTotalAnalyses) * 100 : 0;
      const lastWeekAvgScore = lastWeekTotalAnalyses > 0 
        ? (lastWeekAnalyses?.reduce((sum, a) => sum + (a.overall_score || 0), 0) || 0) / lastWeekTotalAnalyses 
        : 0;

      // Calculate week-over-week percentage changes
      const totalAnalysesTrend = lastWeekTotalAnalyses > 0 
        ? ((thisWeekTotalAnalyses - lastWeekTotalAnalyses) / lastWeekTotalAnalyses) * 100 
        : 0;
      const launchReadyCreativesTrend = lastWeekLaunchReadyCreatives > 0 
        ? ((thisWeekLaunchReadyCreatives - lastWeekLaunchReadyCreatives) / lastWeekLaunchReadyCreatives) * 100 
        : 0;
      const launchReadyRateTrend = lastWeekLaunchReadyRate > 0 
        ? ((thisWeekLaunchReadyRate - lastWeekLaunchReadyRate) / lastWeekLaunchReadyRate) * 100 
        : 0;
      const averagePerformanceScoreTrend = lastWeekAvgScore > 0 
        ? ((thisWeekAvgScore - lastWeekAvgScore) / lastWeekAvgScore) * 100 
        : 0;

      return {
        totalAnalysesTrend: Math.round(totalAnalysesTrend * 10) / 10,
        launchReadyCreativesTrend: Math.round(launchReadyCreativesTrend * 10) / 10,
        launchReadyRateTrend: Math.round(launchReadyRateTrend * 10) / 10,
        averagePerformanceScoreTrend: Math.round(averagePerformanceScoreTrend * 10) / 10
      };

    } catch (error) {
      console.error('Error calculating 7-day trends:', error);
      return {
        totalAnalysesTrend: 0,
        launchReadyCreativesTrend: 0,
        launchReadyRateTrend: 0,
        averagePerformanceScoreTrend: 0
      };
    }
  }
}

export default ProjectService; 